import { useState, useRef, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
    Plus,
    MoreHorizontal,
    GripVertical,
    Trash2,
    Palette,
    Check,
    ChevronDown,
    Settings2,
    Loader2,
} from 'lucide-react'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'


interface BeatBoardContentProps {
    draftId: string
}

// API types
interface ApiBeatBoard {
    id: string
    projectId: string
    name: string
    description: string | null
    structure: {
        acts: Array<{ id: string; name: string; color: string }>
        viewMode?: string
        zoom?: number
    } | null
    beats: ApiBeat[]
    createdAt: number
    updatedAt: number
}

interface ApiBeat {
    id: string
    beatBoardId: string
    title: string
    description: string | null
    act: number | null
    sequence: number | null
    positionX: number
    positionY: number
    sortOrder: number
    color: string | null
    status: 'outline' | 'draft' | 'complete'
    linkedScriptElementId: string | null
    createdAt: number
    updatedAt: number
}

// Local UI types
interface Beat {
    id: string
    title: string
    description: string
    actId: string
    color: string | null
    status: 'outline' | 'draft' | 'complete'
    order: number
}

interface Act {
    id: string
    name: string
    color: string
    order: number
}

// Curated color palette
const COLOR_PALETTE = [
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Indigo', value: '#6366f1' },
    { name: 'Violet', value: '#8b5cf6' },
    { name: 'Purple', value: '#a855f7' },
    { name: 'Pink', value: '#ec4899' },
    { name: 'Rose', value: '#f43f5e' },
    { name: 'Red', value: '#ef4444' },
    { name: 'Orange', value: '#f97316' },
    { name: 'Amber', value: '#f59e0b' },
    { name: 'Yellow', value: '#eab308' },
    { name: 'Lime', value: '#84cc16' },
    { name: 'Green', value: '#22c55e' },
    { name: 'Teal', value: '#14b8a6' },
    { name: 'Cyan', value: '#06b6d4' },
    { name: 'Slate', value: '#64748b' },
]

const STATUS_OPTIONS = [
    { value: 'outline' as const, label: 'Outline', color: 'bg-zinc-500/20 text-zinc-400' },
    { value: 'draft' as const, label: 'Draft', color: 'bg-yellow-500/20 text-yellow-400' },
    { value: 'complete' as const, label: 'Complete', color: 'bg-green-500/20 text-green-400' },
]

const DEFAULT_ACTS: Act[] = [
    { id: 'act-1', name: 'Act One - Setup', color: '#3b82f6', order: 0 },
    { id: 'act-2', name: 'Act Two - Confrontation', color: '#8b5cf6', order: 1 },
    { id: 'act-3', name: 'Act Three - Resolution', color: '#f97316', order: 2 },
]

export default function BeatBoardContent({ draftId: _draftId }: BeatBoardContentProps) {
    const { projectId } = useParams<{ projectId: string }>()
    const queryClient = useQueryClient()

    // Fetch beat boards for this project
    const { data: beatBoardsData, isLoading: loadingBoards } = useQuery({
        queryKey: ['beat-boards', projectId],
        queryFn: () => api.get<Array<ApiBeatBoard & { beatsCount: number }>>(`/projects/${projectId}/beat-boards`),
        enabled: !!projectId,
    })

    const beatBoards = beatBoardsData?.data || []
    const [activeBoardId, setActiveBoardId] = useState<string | null>(null)

    // Auto-select first board
    useEffect(() => {
        if (beatBoards.length > 0 && !activeBoardId) {
            setActiveBoardId(beatBoards[0].id)
        }
    }, [beatBoards, activeBoardId])

    // Fetch active board with beats
    const { data: boardData, isLoading: loadingBoard } = useQuery({
        queryKey: ['beat-board', projectId, activeBoardId],
        queryFn: () => api.get<ApiBeatBoard>(`/projects/${projectId}/beat-boards/${activeBoardId}`),
        enabled: !!projectId && !!activeBoardId,
    })

    const boardDetail = boardData?.data

    // Map API data to local UI state
    const apiActs = boardDetail?.structure?.acts || []
    const acts: Act[] = apiActs.length > 0
        ? apiActs.map((a, i) => ({ id: a.id, name: a.name, color: a.color, order: i }))
        : DEFAULT_ACTS

    const beats: Beat[] = (boardDetail?.beats || []).map(b => ({
        id: b.id,
        title: b.title,
        description: b.description || '',
        actId: acts[b.act ? b.act - 1 : 0]?.id || acts[0]?.id || 'act-1',
        color: b.color,
        status: b.status,
        order: b.sortOrder,
    }))

    // -- Mutations --

    // Create beat board
    const createBoardMutation = useMutation({
        mutationFn: () => api.post(`/projects/${projectId}/beat-boards`, {
            name: 'Beat Board',
            description: null,
        }),
        onSuccess: (res: any) => {
            queryClient.invalidateQueries({ queryKey: ['beat-boards', projectId] })
            setActiveBoardId(res.data.id)
            // Set default acts structure
            updateStructureMutation.mutate({
                boardId: res.data.id,
                structure: JSON.stringify({
                    acts: DEFAULT_ACTS.map(a => ({ id: a.id, name: a.name, color: a.color })),
                    viewMode: 'board',
                    zoom: 1,
                }),
            })
        },
    })

    // Update board structure (acts)
    const updateStructureMutation = useMutation({
        mutationFn: ({ boardId, structure }: { boardId: string; structure: string }) =>
            api.patch(`/projects/${projectId}/beat-boards/${boardId}`, { structure }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['beat-board', projectId, activeBoardId] })
        },
    })

    // Create beat
    const createBeatMutation = useMutation({
        mutationFn: (data: { title: string; act: number; color?: string }) =>
            api.post(`/projects/${projectId}/beat-boards/${activeBoardId}/beats`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['beat-board', projectId, activeBoardId] })
        },
    })

    // Update beat
    const updateBeatMutation = useMutation({
        mutationFn: ({ beatId, data }: { beatId: string; data: Record<string, unknown> }) =>
            api.patch(`/projects/${projectId}/beat-boards/${activeBoardId}/beats/${beatId}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['beat-board', projectId, activeBoardId] })
        },
    })

    // Delete beat
    const deleteBeatMutation = useMutation({
        mutationFn: (beatId: string) =>
            api.delete(`/projects/${projectId}/beat-boards/${activeBoardId}/beats/${beatId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['beat-board', projectId, activeBoardId] })
        },
    })

    // Reorder beats
    const reorderBeatsMutation = useMutation({
        mutationFn: (reorderData: { beats: Array<{ id: string; sortOrder: number }> }) =>
            api.post(`/projects/${projectId}/beat-boards/${activeBoardId}/beats/reorder`, reorderData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['beat-board', projectId, activeBoardId] })
        },
    })

    // UI state
    const [editingBeat, setEditingBeat] = useState<string | null>(null)
    const [editingAct, setEditingAct] = useState<string | null>(null)
    const [draggedBeat, setDraggedBeat] = useState<string | null>(null)
    const [dragOverTarget, setDragOverTarget] = useState<{ actId: string; insertIndex: number } | null>(null)
    const editInputRef = useRef<HTMLInputElement>(null)

    // Focus input when editing starts
    useEffect(() => {
        if ((editingBeat || editingAct) && editInputRef.current) {
            editInputRef.current.focus()
            editInputRef.current.select()
        }
    }, [editingBeat, editingAct])

    const getBeatsForAct = useCallback((actId: string) =>
        beats.filter(b => b.actId === actId).sort((a, b) => a.order - b.order),
        [beats])

    const getActColor = (actId: string) =>
        acts.find(a => a.id === actId)?.color || '#6366f1'

    const getBeatColor = (beat: Beat) =>
        beat.color || getActColor(beat.actId)

    const getActNumber = (actId: string) => {
        const idx = acts.findIndex(a => a.id === actId)
        return idx >= 0 ? idx + 1 : 1
    }

    // -- Handlers --

    const handleAddBeat = (actId: string) => {

        createBeatMutation.mutate({
            title: 'New Beat',
            act: getActNumber(actId),
        })
        // Optimistically set editing on success — handled via onSuccess refetch
    }

    const handleAddAct = () => {
        const newAct = {
            id: crypto.randomUUID(),
            name: `Act ${acts.length + 1}`,
            color: COLOR_PALETTE[acts.length % COLOR_PALETTE.length].value,
        }
        const newActs = [...acts.map(a => ({ id: a.id, name: a.name, color: a.color })), newAct]
        if (activeBoardId) {
            updateStructureMutation.mutate({
                boardId: activeBoardId,
                structure: JSON.stringify({ acts: newActs, viewMode: 'board', zoom: 1 }),
            })
        }
    }

    const handleDeleteBeat = (beatId: string) => {
        deleteBeatMutation.mutate(beatId)
    }

    const handleDeleteAct = (actId: string) => {
        if (acts.length <= 1) return
        const actIndex = acts.findIndex(a => a.id === actId)
        const prevAct = actIndex > 0 ? acts[actIndex - 1] : acts[actIndex + 1]

        // Move beats to adjacent act
        const actBeats = getBeatsForAct(actId)
        if (prevAct && actBeats.length > 0) {
            const targetActNum = getActNumber(prevAct.id)
            actBeats.forEach(beat => {
                updateBeatMutation.mutate({
                    beatId: beat.id,
                    data: { act: targetActNum },
                })
            })
        }

        // Remove act from structure
        const newActs = acts.filter(a => a.id !== actId).map(a => ({ id: a.id, name: a.name, color: a.color }))
        if (activeBoardId) {
            updateStructureMutation.mutate({
                boardId: activeBoardId,
                structure: JSON.stringify({ acts: newActs, viewMode: 'board', zoom: 1 }),
            })
        }
    }

    const handleUpdateBeat = (beatId: string, updates: Partial<Beat>) => {
        const apiUpdates: Record<string, unknown> = {}
        if (updates.title !== undefined) apiUpdates.title = updates.title
        if (updates.description !== undefined) apiUpdates.description = updates.description
        if (updates.color !== undefined) apiUpdates.color = updates.color
        if (updates.status !== undefined) apiUpdates.status = updates.status
        if (updates.actId !== undefined) apiUpdates.act = getActNumber(updates.actId)
        if (updates.order !== undefined) apiUpdates.sortOrder = updates.order

        updateBeatMutation.mutate({ beatId, data: apiUpdates })
    }

    const handleUpdateAct = (actId: string, updates: Partial<Act>) => {
        const newActs = acts.map(a => {
            if (a.id === actId) {
                return {
                    id: a.id,
                    name: updates.name !== undefined ? updates.name : a.name,
                    color: updates.color !== undefined ? updates.color : a.color,
                }
            }
            return { id: a.id, name: a.name, color: a.color }
        })
        if (activeBoardId) {
            updateStructureMutation.mutate({
                boardId: activeBoardId,
                structure: JSON.stringify({ acts: newActs, viewMode: 'board', zoom: 1 }),
            })
        }
    }

    // -- Drag and drop with within-act reorder --

    const handleDragStart = (e: React.DragEvent, beatId: string) => {
        setDraggedBeat(beatId)
        e.dataTransfer.effectAllowed = 'move'
        // Set drag image
        const el = e.currentTarget as HTMLElement
        e.dataTransfer.setDragImage(el, el.offsetWidth / 2, 20)
    }

    const handleBeatDragOver = (e: React.DragEvent, actId: string, insertIndex: number) => {
        e.preventDefault()
        e.stopPropagation()
        setDragOverTarget({ actId, insertIndex })
    }

    const handleActDragOver = (e: React.DragEvent, actId: string) => {
        e.preventDefault()
        const actBeats = getBeatsForAct(actId)
        setDragOverTarget({ actId, insertIndex: actBeats.length })
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        if (!draggedBeat || !dragOverTarget) {
            setDraggedBeat(null)
            setDragOverTarget(null)
            return
        }

        const beat = beats.find(b => b.id === draggedBeat)
        if (!beat) {
            setDraggedBeat(null)
            setDragOverTarget(null)
            return
        }

        const targetActId = dragOverTarget.actId
        const insertIndex = dragOverTarget.insertIndex

        // Get the beats in the target act, excluding the dragged beat
        const targetBeats = getBeatsForAct(targetActId).filter(b => b.id !== draggedBeat)

        // Insert at the correct position
        const reorderedBeats = [...targetBeats]
        const adjustedIndex = Math.min(insertIndex, reorderedBeats.length)

        // Create the moved beat entry
        const movedBeat = { ...beat, actId: targetActId }
        reorderedBeats.splice(adjustedIndex, 0, movedBeat)

        // If beat came from a different act, also reorder the source act
        const sourceActId = beat.actId
        const allReorderedBeats: Array<{ id: string; sortOrder: number }> = []

        // Reorder target act beats
        reorderedBeats.forEach((b, i) => {
            allReorderedBeats.push({ id: b.id, sortOrder: i })
        })

        // If source act is different, reorder source act beats too
        if (sourceActId !== targetActId) {
            const sourceBeats = getBeatsForAct(sourceActId).filter(b => b.id !== draggedBeat)
            sourceBeats.forEach((b, i) => {
                allReorderedBeats.push({ id: b.id, sortOrder: i })
            })

            // Update the act number for the moved beat
            handleUpdateBeat(draggedBeat, { actId: targetActId })
        }

        // Send reorder to API
        if (allReorderedBeats.length > 0) {
            reorderBeatsMutation.mutate({ beats: allReorderedBeats })
        }

        setDraggedBeat(null)
        setDragOverTarget(null)
    }

    const handleDragEnd = () => {
        setDraggedBeat(null)
        setDragOverTarget(null)
    }

    // Auto-create a board if none exists
    useEffect(() => {
        if (!loadingBoards && beatBoards.length === 0 && projectId) {
            createBoardMutation.mutate()
        }
    }, [loadingBoards, beatBoards.length, projectId])

    // Loading state
    if (loadingBoards || loadingBoard) {
        return (
            <div className="h-full flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        )
    }

    return (
        <div className="h-full flex flex-col bg-background">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
                <div>
                    <h1 className="text-lg font-semibold">Beat Board</h1>
                    <p className="text-xs text-muted-foreground">{beats.length} beats across {acts.length} acts</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleAddAct}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Act
                </Button>
            </div>

            {/* Acts Grid */}
            <div className="flex-1 overflow-x-auto p-6">
                <div className="flex gap-4 h-full min-w-max">
                    {acts.sort((a, b) => a.order - b.order).map((act) => {
                        const actBeats = getBeatsForAct(act.id)
                        return (
                            <div
                                key={act.id}
                                className={cn(
                                    "flex flex-col w-80 min-h-0 rounded-lg border transition-all",
                                    dragOverTarget?.actId === act.id && "ring-2 ring-primary"
                                )}
                                onDragOver={(e) => handleActDragOver(e, act.id)}
                                onDrop={handleDrop}
                                onDragLeave={() => setDragOverTarget(null)}
                            >
                                {/* Act Header */}
                                <div
                                    className="px-3 py-2 rounded-t-lg border-b flex items-center gap-2"
                                    style={{ backgroundColor: `${act.color}15`, borderColor: `${act.color}30` }}
                                >
                                    {editingAct === act.id ? (
                                        <Input
                                            ref={editInputRef}
                                            defaultValue={act.name}
                                            onBlur={(e) => {
                                                handleUpdateAct(act.id, { name: e.target.value })
                                                setEditingAct(null)
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleUpdateAct(act.id, { name: (e.target as HTMLInputElement).value })
                                                    setEditingAct(null)
                                                }
                                            }}
                                            className="h-7 text-sm font-medium bg-background/80"
                                        />
                                    ) : (
                                        <>
                                            <div
                                                className="w-3 h-3 rounded-full flex-shrink-0"
                                                style={{ backgroundColor: act.color }}
                                            />
                                            <span
                                                className="flex-1 text-sm font-medium cursor-pointer hover:underline"
                                                onClick={() => setEditingAct(act.id)}
                                            >
                                                {act.name}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                                {actBeats.length}
                                            </span>
                                        </>
                                    )}

                                    {/* Act Menu */}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-6 w-6">
                                                <Settings2 className="h-3 w-3" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => setEditingAct(act.id)}>
                                                Rename
                                            </DropdownMenuItem>
                                            {/* Color picker submenu */}
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                                        <Palette className="h-4 w-4 mr-2" />
                                                        Change Color
                                                    </DropdownMenuItem>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-48 p-2" side="right">
                                                    <div className="grid grid-cols-5 gap-1">
                                                        {COLOR_PALETTE.map((color) => (
                                                            <button
                                                                key={color.value}
                                                                className={cn(
                                                                    "w-7 h-7 rounded-md flex items-center justify-center transition-transform hover:scale-110",
                                                                    act.color === color.value && "ring-2 ring-white ring-offset-2 ring-offset-background"
                                                                )}
                                                                style={{ backgroundColor: color.value }}
                                                                onClick={() => handleUpdateAct(act.id, { color: color.value })}
                                                                title={color.name}
                                                            >
                                                                {act.color === color.value && (
                                                                    <Check className="h-3 w-3 text-white" />
                                                                )}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </PopoverContent>
                                            </Popover>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                                className="text-destructive"
                                                onClick={() => handleDeleteAct(act.id)}
                                                disabled={acts.length <= 1}
                                            >
                                                <Trash2 className="h-4 w-4 mr-2" />
                                                Delete Act
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>

                                {/* Beat Cards */}
                                <div className="flex-1 overflow-y-auto p-2 space-y-1 bg-card/30">
                                    {actBeats.map((beat, beatIndex) => (
                                        <div key={beat.id}>
                                            {/* Drop zone above this beat */}
                                            <div
                                                className={cn(
                                                    "h-1 rounded-full transition-all mx-1",
                                                    dragOverTarget?.actId === act.id && dragOverTarget?.insertIndex === beatIndex
                                                        ? "bg-primary h-1.5 my-1"
                                                        : "bg-transparent"
                                                )}
                                                onDragOver={(e) => handleBeatDragOver(e, act.id, beatIndex)}
                                            />

                                            <div
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, beat.id)}
                                                onDragEnd={handleDragEnd}
                                                className={cn(
                                                    "p-3 rounded-lg border bg-card cursor-grab transition-all hover:shadow-md group",
                                                    draggedBeat === beat.id && "opacity-50 cursor-grabbing"
                                                )}
                                                style={{ borderLeftWidth: '3px', borderLeftColor: getBeatColor(beat) }}
                                            >
                                                <div className="flex items-start gap-2">
                                                    <GripVertical className="h-4 w-4 text-muted-foreground mt-0.5 opacity-0 group-hover:opacity-100 cursor-grab" />
                                                    <div className="flex-1 min-w-0">
                                                        {editingBeat === beat.id ? (
                                                            <div className="space-y-2">
                                                                <Input
                                                                    ref={editInputRef}
                                                                    defaultValue={beat.title}
                                                                    className="h-7 text-sm font-medium"
                                                                    placeholder="Beat title"
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            handleUpdateBeat(beat.id, { title: (e.target as HTMLInputElement).value })
                                                                            setEditingBeat(null)
                                                                        }
                                                                    }}
                                                                />
                                                                <Input
                                                                    defaultValue={beat.description}
                                                                    className="h-7 text-xs"
                                                                    placeholder="Description (optional)"
                                                                    onBlur={(e) => {
                                                                        handleUpdateBeat(beat.id, { description: e.target.value })
                                                                        setEditingBeat(null)
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            handleUpdateBeat(beat.id, { description: (e.target as HTMLInputElement).value })
                                                                            setEditingBeat(null)
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div onClick={() => setEditingBeat(beat.id)} className="cursor-text">
                                                                <h3 className="text-sm font-medium truncate">{beat.title}</h3>
                                                                {beat.description && (
                                                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                                                        {beat.description}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Status & Actions */}
                                                        <div className="flex items-center gap-2 mt-2">
                                                            {/* Status Dropdown */}
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <button className={cn(
                                                                        "text-[10px] px-1.5 py-0.5 rounded capitalize flex items-center gap-1",
                                                                        STATUS_OPTIONS.find(s => s.value === beat.status)?.color
                                                                    )}>
                                                                        {beat.status}
                                                                        <ChevronDown className="h-2.5 w-2.5" />
                                                                    </button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent>
                                                                    {STATUS_OPTIONS.map((status) => (
                                                                        <DropdownMenuItem
                                                                            key={status.value}
                                                                            onClick={() => handleUpdateBeat(beat.id, { status: status.value })}
                                                                        >
                                                                            <span className={cn("text-xs px-1.5 py-0.5 rounded", status.color)}>
                                                                                {status.label}
                                                                            </span>
                                                                        </DropdownMenuItem>
                                                                    ))}
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>

                                                            <div className="flex-1" />

                                                            {/* Beat Color Picker */}
                                                            <Popover>
                                                                <PopoverTrigger asChild>
                                                                    <button
                                                                        className="h-4 w-4 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                                                        style={{ backgroundColor: getBeatColor(beat) }}
                                                                        title="Change color"
                                                                    />
                                                                </PopoverTrigger>
                                                                <PopoverContent className="w-48 p-2" side="right">
                                                                    <div className="grid grid-cols-5 gap-1">
                                                                        {/* Inherit option */}
                                                                        <button
                                                                            className={cn(
                                                                                "w-7 h-7 rounded-md flex items-center justify-center border-2 border-dashed text-xs",
                                                                                beat.color === null && "ring-2 ring-primary"
                                                                            )}
                                                                            onClick={() => handleUpdateBeat(beat.id, { color: null })}
                                                                            title="Inherit from act"
                                                                        >
                                                                            A
                                                                        </button>
                                                                        {COLOR_PALETTE.slice(0, 14).map((color) => (
                                                                            <button
                                                                                key={color.value}
                                                                                className={cn(
                                                                                    "w-7 h-7 rounded-md flex items-center justify-center transition-transform hover:scale-110",
                                                                                    beat.color === color.value && "ring-2 ring-white ring-offset-2 ring-offset-background"
                                                                                )}
                                                                                style={{ backgroundColor: color.value }}
                                                                                onClick={() => handleUpdateBeat(beat.id, { color: color.value })}
                                                                                title={color.name}
                                                                            >
                                                                                {beat.color === color.value && (
                                                                                    <Check className="h-3 w-3 text-white" />
                                                                                )}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                </PopoverContent>
                                                            </Popover>

                                                            {/* More Menu */}
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-5 w-5 opacity-0 group-hover:opacity-100"
                                                                    >
                                                                        <MoreHorizontal className="h-3 w-3" />
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    <DropdownMenuItem onClick={() => setEditingBeat(beat.id)}>
                                                                        Edit
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuSeparator />
                                                                    <DropdownMenuItem
                                                                        className="text-destructive"
                                                                        onClick={() => handleDeleteBeat(beat.id)}
                                                                    >
                                                                        <Trash2 className="h-4 w-4 mr-2" />
                                                                        Delete
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Drop zone at the end of the list */}
                                    <div
                                        className={cn(
                                            "h-1 rounded-full transition-all mx-1",
                                            dragOverTarget?.actId === act.id && dragOverTarget?.insertIndex === actBeats.length
                                                ? "bg-primary h-1.5 my-1"
                                                : "bg-transparent"
                                        )}
                                        onDragOver={(e) => handleBeatDragOver(e, act.id, actBeats.length)}
                                    />

                                    {/* Add Beat Button */}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full h-8 border-dashed"
                                        onClick={() => handleAddBeat(act.id)}
                                        disabled={createBeatMutation.isPending}
                                    >
                                        {createBeatMutation.isPending ? (
                                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                        ) : (
                                            <Plus className="h-3 w-3 mr-1" />
                                        )}
                                        Add Beat
                                    </Button>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
